// LibraryOverview.jsx — read-only Library visibility for Admin and Finance.
// All actual catalog/issue/fine management happens on the Librarian's own
// portal (LibraryPages.jsx) — this is oversight only.
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  BookOpenIcon, ArrowsRightLeftIcon, ExclamationTriangleIcon,
  CheckCircleIcon, ClockIcon,
} from '@heroicons/react/24/outline';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .lo * { box-sizing:border-box; }
  .lo { font-family:'Sora',sans-serif; color:#1a1d2e; animation:loFade .4s ease both; }
  @keyframes loFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .lo-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .lo-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; margin-bottom:1.4rem; }
  .stat-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:1rem; margin-bottom:1.4rem; }
  @media (max-width:1000px) { .stat-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:640px)  { .stat-grid { grid-template-columns:repeat(2,1fr); } }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.2rem; border:1px solid #e8ebf4; }
  .sc-icon  { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.7rem; }
  .sc-icon svg { width:16px; height:16px; stroke-width:1.8; }
  .sc-icon.amber { background:#fffbeb; color:#f59e0b; }
  .sc-icon.blue  { background:#eef2ff; color:#3b6ef8; }
  .sc-icon.red   { background:#fef2f2; color:#ef4444; }
  .sc-icon.green { background:#ecfdf5; color:#10c98e; }
  .sc-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sc-value { font-size:1.3rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1.2; margin-top:2px; }
  .tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .tab  { padding:6px 14px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.75rem; font-weight:600; cursor:pointer; color:#6b7194; }
  .tab.active { background:#f59e0b; color:white; border-color:#f59e0b; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 14px; text-align:center; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; }
  .tbl td { padding:10px 14px; font-size:.8rem; text-align:center; vertical-align:middle; border-bottom:1px solid #f3f5fb; }
  .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:180px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#f59e0b; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .pill { font-size:.66rem; font-weight:600; padding:3px 9px; border-radius:99px; display:inline-flex; white-space:nowrap; }
  .pill-issued{background:#eef2ff;color:#3b6ef8;} .pill-returned{background:#ecfdf5;color:#10c98e;} .pill-lost{background:#fef2f2;color:#ef4444;}
  .pill-pending{background:#fffbeb;color:#f59e0b;} .pill-applied{background:#ecfdf5;color:#10c98e;}
`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'}) : '—';
// "Grade 10 — A" (dash skipped when there is no section)
const classLabel = (name, section) => (name && section) ? `${name} — ${section}` : (name || section || '—');

const LibraryOverview = ({ role = 'admin' }) => {
  const base = `/${role}/library`;
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('books');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`${base}/overview`).then(r => setStats(r.data)).catch(()=>{});
  }, [base]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`${base}/${tab}`).then(r => setRows(r.data||[])).catch(()=>toast.error('Failed to load')).finally(()=>setLoading(false));
  }, [base, tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="lo"><style>{css}</style>
      <div className="lo-title">Library</div>
      <div className="lo-sub">Read-only oversight — book catalog, issues and fines are managed by your Librarian.</div>

      {stats && (
        <div className="stat-grid">
          <div className="stat-card"><div className="sc-icon amber"><BookOpenIcon/></div><div className="sc-label">Books / Copies</div><div className="sc-value">{stats.totalBooks} / {stats.totalCopies}</div></div>
          <div className="stat-card"><div className="sc-icon blue"><ArrowsRightLeftIcon/></div><div className="sc-label">Issued Out</div><div className="sc-value">{stats.issuedOut}</div></div>
          <div className="stat-card"><div className="sc-icon red"><ExclamationTriangleIcon/></div><div className="sc-label">Overdue</div><div className="sc-value">{stats.overdueCount}</div></div>
          <div className="stat-card"><div className="sc-icon amber"><ClockIcon/></div><div className="sc-label">Pending Fines</div><div className="sc-value">{stats.pendingFines}</div></div>
          <div className="stat-card"><div className="sc-icon green"><CheckCircleIcon/></div><div className="sc-label">Fines Total</div><div className="sc-value">Rs. {Number(stats.finesTotal).toLocaleString()}</div></div>
        </div>
      )}

      <div className="tabs">
        <button className={`tab ${tab==='books'?'active':''}`} onClick={()=>setTab('books')}>Books</button>
        <button className={`tab ${tab==='fines'?'active':''}`} onClick={()=>setTab('fines')}>Fines</button>
        <button className={`tab ${tab==='issues'?'active':''}`} onClick={()=>setTab('issues')}>Issues</button>
      </div>

      <div className="panel">
        {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
        : rows.length===0 ? <div className="empty">Nothing here yet.</div>
        : tab === 'books' ? (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Title</th><th>Author</th><th>ISBN</th><th>Publisher</th><th>Category</th><th>Shelf</th><th>Copies</th></tr></thead>
              <tbody>{rows.map(b => (
                <tr key={b._id}><td style={{textAlign:'left',fontWeight:600}}>{b.title}</td><td>{b.author||'—'}</td><td>{b.isbn||'—'}</td><td>{b.publisher||'—'}</td><td>{b.category}</td><td>{b.shelfLocation||'—'}</td>
                  <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>{b.availableCopies}/{b.totalCopies}</td></tr>
              ))}</tbody>
            </table>
          </div>
        ) : tab === 'issues' ? (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Book</th><th>Student</th><th>Class</th><th>Issued</th><th>Due</th><th>Status</th></tr></thead>
              <tbody>{rows.map(i => (
                <tr key={i._id}><td style={{textAlign:'left',fontWeight:600}}>{i.bookTitle}</td><td>{i.studentName}</td><td>{classLabel(i.className, i.section)}</td>
                  <td>{fmtDate(i.issueDate)}</td><td>{fmtDate(i.dueDate)}</td>
                  <td>{i.isOverdue ? <span className="pill pill-lost">Overdue</span> : <span className={`pill pill-${i.status}`}>{i.status}</span>}</td></tr>
              ))}</tbody>
            </table>
          </div>
        ) : (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Student</th><th>Class</th><th>Reason</th><th>Amount</th><th>Month</th><th>Status</th></tr></thead>
              <tbody>{rows.map(f => (
                <tr key={f._id}><td style={{textAlign:'left',fontWeight:600}}>{f.studentName}</td><td>{classLabel(f.className, f.section)}</td><td>{f.reason}</td>
                  <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>Rs. {Number(f.amount).toLocaleString()}</td><td>{f.month}</td>
                  <td><span className={`pill pill-${f.status}`}>{f.status==='applied'?'Applied':'Pending'}</span></td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LibraryOverview;
